import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Typography,
  Alert,
  Snackbar,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material'
import {
  Delete,
  ArrowUpward,
  ArrowDownward,
  AddPhotoAlternate,
  Edit as EditIcon,
} from '@mui/icons-material'
import { useInvestmentContext } from '../contexts/InvestmentContext'
import { supabase } from '../supabaseClient'

const BUCKET = 'property-media'

type Media = { id: string; file_url: string; order: number }
type Snack = { sev: 'success' | 'error'; msg: string }

// Dialog wyboru zdjęcia. Trzy źródła: pliki już wgrane do property-media/<slug>/,
// upload nowego pliku (append-only INSERT dozwolony przez storage RLS) i wklejenie URL.
// Zwraca wybrany file_url przez onPick.
function PhotoPicker({
  slug,
  open,
  onClose,
  onPick,
  onError,
}: {
  slug: string
  open: boolean
  onClose: () => void
  onPick: (url: string) => void
  onError: (msg: string) => void
}) {
  const [files, setFiles] = useState<{ name: string; url: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [url, setUrl] = useState('')

  const loadFiles = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .list(slug, { limit: 300, sortBy: { column: 'name', order: 'asc' } })
      if (error) throw error
      setFiles(
        (data || [])
          .filter((f) => !f.id?.endsWith('/'))
          .map((f) => ({
            name: f.name,
            url: supabase.storage.from(BUCKET).getPublicUrl(`${slug}/${f.name}`).data.publicUrl,
          })),
      )
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Błąd ładowania plików')
      setFiles([])
    } finally {
      setLoading(false)
    }
  }, [slug, onError])

  useEffect(() => {
    if (open) {
      setUrl('')
      loadFiles()
    }
  }, [open, loadFiles])

  async function upload(file: File) {
    setUploading(true)
    try {
      // Nazwa bezpieczna dla storage; prefix czasowy zapobiega kolizjom.
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const path = `${slug}/${Date.now()}_${safe}`
      const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false })
      if (error) throw error
      const publicUrl = supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl
      onPick(publicUrl)
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Błąd uploadu')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Wybierz zdjęcie</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Button variant="contained" component="label" startIcon={<AddPhotoAlternate />} disabled={uploading}>
            {uploading ? 'Wgrywanie…' : 'Wgraj plik'}
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) upload(f)
                e.target.value = ''
              }}
            />
          </Button>
          {uploading && <CircularProgress size={20} />}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            label="…lub wklej URL zdjęcia"
            size="small"
            fullWidth
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Button variant="outlined" disabled={!url.trim()} onClick={() => onPick(url.trim())}>
            Użyj
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          Pliki w storage: {slug}/
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : files.length === 0 ? (
          <Alert severity="info">Brak plików w storage tej inwestycji. Wgraj nowy plik powyżej.</Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 1.5,
            }}
          >
            {files.map((f) => (
              <Paper
                key={f.name}
                variant="outlined"
                sx={{ p: 0.5, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                onClick={() => onPick(f.url)}
              >
                <Box
                  component="img"
                  src={f.url}
                  alt={f.name}
                  sx={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 1, display: 'block' }}
                />
                <Typography variant="caption" noWrap display="block" title={f.name}>
                  {f.name}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zamknij</Button>
      </DialogActions>
    </Dialog>
  )
}

export function InvestmentPhotosPage() {
  const { currentInvestment, patchInvestment } = useInvestmentContext()
  const [medias, setMedias] = useState<Media[]>([])
  const [loadingMedias, setLoadingMedias] = useState(false)
  const [busy, setBusy] = useState(false)
  const [snack, setSnack] = useState<Snack | null>(null)
  // picker mode: 'main' = ustaw zdjęcie główne, 'add' = dodaj dodatkowe
  const [picker, setPicker] = useState<null | 'main' | 'add'>(null)

  const slug = currentInvestment?.slug ?? ''
  const label = currentInvestment?.panel_display_label || currentInvestment?.name || ''
  const mainImageUrl = (currentInvestment as { main_image_url?: string | null } | null)?.main_image_url ?? null

  const loadMedias = useCallback(async () => {
    if (!currentInvestment) return
    setLoadingMedias(true)
    const { data, error } = await supabase
      .from('investment_medias')
      .select('id, file_url, order')
      .eq('investment_id', currentInvestment.id)
      .order('order')
    setLoadingMedias(false)
    if (error) setSnack({ sev: 'error', msg: error.message })
    else setMedias((data as Media[]) ?? [])
  }, [currentInvestment])

  useEffect(() => {
    setMedias([])
    loadMedias()
  }, [loadMedias])

  // --- Zdjęcie główne ---
  async function setMain(url: string) {
    if (!currentInvestment) return
    setBusy(true)
    const { data, error } = await supabase.rpc('update_investment_portfolio', {
      p_investment_id: currentInvestment.id,
      p_main_image_url: url,
    })
    setBusy(false)
    const result = data as { success?: boolean; error?: string } | null
    if (error || result?.success === false) {
      setSnack({ sev: 'error', msg: error?.message || result?.error || 'Błąd zapisu' })
    } else {
      patchInvestment(currentInvestment.id, { main_image_url: url } as Partial<typeof currentInvestment>)
      setSnack({ sev: 'success', msg: 'Zapisano zdjęcie główne' })
    }
  }

  // --- Zdjęcia dodatkowe ---
  async function addMedia(url: string) {
    if (!currentInvestment) return
    setBusy(true)
    const { error } = await supabase.rpc('insert_investment_media_last', {
      p_investment_id: currentInvestment.id,
      p_file_url: url,
    })
    setBusy(false)
    if (error) setSnack({ sev: 'error', msg: error.message })
    else {
      setSnack({ sev: 'success', msg: 'Dodano zdjęcie' })
      loadMedias()
    }
  }

  async function deleteMediaAt(position: number) {
    if (!currentInvestment) return
    setBusy(true)
    const { error } = await supabase.rpc('delete_investment_media_at', {
      p_investment_id: currentInvestment.id,
      p_position: position,
    })
    setBusy(false)
    if (error) setSnack({ sev: 'error', msg: error.message })
    else loadMedias()
  }

  // Reorder przez zamianę file_url między dwiema pozycjami (1-based) — nie ruszamy
  // kolumny "order", więc nie kolidujemy z DEFERRABLE unique constraint.
  async function swap(posA: number, posB: number) {
    if (!currentInvestment) return
    const a = medias[posA - 1]
    const b = medias[posB - 1]
    if (!a || !b) return
    setBusy(true)
    const r1 = await supabase.rpc('update_investment_media_at', {
      p_investment_id: currentInvestment.id,
      p_position: posA,
      p_file_url: b.file_url,
    })
    const r2 = await supabase.rpc('update_investment_media_at', {
      p_investment_id: currentInvestment.id,
      p_position: posB,
      p_file_url: a.file_url,
    })
    setBusy(false)
    if (r1.error || r2.error) setSnack({ sev: 'error', msg: r1.error?.message || r2.error?.message || 'Błąd' })
    else loadMedias()
  }

  function onPick(url: string) {
    const mode = picker
    setPicker(null)
    if (!url) return
    if (mode === 'main') setMain(url)
    else if (mode === 'add') addMedia(url)
  }

  if (!currentInvestment) {
    return (
      <Box p={3}>
        <Alert severity="info">Wybierz inwestycję w menu po lewej.</Alert>
      </Box>
    )
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Zdjęcia — {label}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Zdjęcie główne i zdjęcia dodatkowe wyświetlane w portfolio (kafelki i galeria).
      </Typography>

      {/* Zdjęcie główne */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
        Zdjęcie główne
      </Typography>
      <Paper variant="outlined" sx={{ p: 2, mb: 4, maxWidth: 900 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <Box
            sx={{
              width: 260,
              height: 170,
              flexShrink: 0,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {mainImageUrl ? (
              <Box component="img" src={mainImageUrl} alt="główne" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Typography variant="body2" color="textSecondary">
                Brak zdjęcia
              </Typography>
            )}
          </Box>
          <Stack spacing={1}>
            <Button variant="contained" startIcon={<EditIcon />} disabled={busy} onClick={() => setPicker('main')}>
              {mainImageUrl ? 'Zmień zdjęcie główne' : 'Ustaw zdjęcie główne'}
            </Button>
            {mainImageUrl && (
              <Typography variant="caption" color="textSecondary" sx={{ wordBreak: 'break-all', maxWidth: 400 }}>
                {mainImageUrl}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Zdjęcia dodatkowe */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h6">Zdjęcia dodatkowe</Typography>
        <Button variant="outlined" startIcon={<AddPhotoAlternate />} disabled={busy} onClick={() => setPicker('add')}>
          Dodaj zdjęcie
        </Button>
        {(busy || loadingMedias) && <CircularProgress size={20} />}
      </Stack>

      {medias.length === 0 && !loadingMedias ? (
        <Alert severity="info" sx={{ maxWidth: 900 }}>
          Brak zdjęć dodatkowych. Dodaj pierwsze zdjęcie.
        </Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
            maxWidth: 900,
          }}
        >
          {medias.map((m, idx) => {
            const pos = idx + 1
            return (
              <Paper key={m.id} variant="outlined" sx={{ p: 1 }}>
                <Box
                  component="img"
                  src={m.file_url}
                  alt={`zdjęcie ${pos}`}
                  sx={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 1, display: 'block', mb: 1 }}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="textSecondary">
                    {pos} / {medias.length}
                  </Typography>
                  <Stack direction="row">
                    <IconButton aria-label="Przesuń w górę" size="small" disabled={busy || idx === 0} onClick={() => swap(pos, pos - 1)}>
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton aria-label="Przesuń w dół" size="small" disabled={busy || idx === medias.length - 1} onClick={() => swap(pos, pos + 1)}>
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                    <IconButton aria-label="Usuń zdjęcie" size="small" color="error" disabled={busy} onClick={() => deleteMediaAt(pos)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Paper>
            )
          })}
        </Box>
      )}

      <PhotoPicker
        slug={slug}
        open={picker !== null}
        onClose={() => setPicker(null)}
        onPick={onPick}
        onError={(msg) => setSnack({ sev: 'error', msg })}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.sev} onClose={() => setSnack(null)} sx={{ width: '100%' }}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}
